import React, { Component } from 'react'
import { graphql, compose, withApollo } from 'react-apollo'
import styled, { css } from 'styled-components'
import Editor from '@sukiai/quill-editor'
import { logger, halfStory as hs } from '../../lib/logging'
import { Constants, Loading } from '@sukiai/components'
import {
  LO_STO,
  SESH_STO,
  setSectionDictation,
  stopSectionDictation,
  SUKI_ROLE,
  HALF_STORY,
  setToast
} from '../../lib'
import {
  GET_AUDIO_PROCESSED,
  SET_AUDIO_PROCESSED,
  SET_AUDIO_STREAM_UI_CONTEXT,
  GET_AUDIO_STREAM_UI_CONTEXT,
  SET_SECTION,
  SET_CURSOR_POSITION,
  GET_CURSOR_POSITION,
  SET_AGENT_STATE,
  SET_KEYBOARD_MODE,
  GET_KEYBOARD_MODE,
  CLEAR_SECTION,
  GET_CURRENT_NOTETYPE_ID,
  SET_CURSOR_ACKNOWLEDGED,
  SET_CONTENT_TO_INSERT,
  GET_CURRENT_COMPOSITION,
  SET_INSERT_SCRIPT_PENDING
} from '../../apollo/actions'
import { getStreamWorker, sendSpeechConfig } from '../../ws/stream'
import { DataCommand } from '../../ws/DataCommand'
import loSto from '../../config/loSto'
import seshSto from '../../config/seshSto'
import { handleContentFocus } from '../../lib/util/scroll'
import { isAmwellTheme, getMacroContent } from '../../lib/util'

const { SECTION_STATE, AGENT, TOAST } = Constants

const Container = styled.div`
  position: relative;

  ${props => props.isAmwellTheme && css`
    .ql-editor {
      line-height: 1.5
    };

    .ql-container.ql-snow {
      font-size: 16px;
    }
  `}
`

class SectionEditor extends Component {
  state = {
    editorVal: this.props.content,
    isAmwellDark: window.localStorage?.getItem('themeMode') === 'dark'
  }

  componentWillReceiveProps (nextProps) {
    const {
      id,
      compositionId,
      currentSectionId,
      currentSectionState,
      updateSectionFlush
    } = this.props

    // If section is read-only and out of date,
    // update with latest content from props
    if (nextProps.content !== this.state.editorVal) {
      console.info('[Editor] Updating SectionEditor content from props')
      logger.info(`[Editor] Updating SectionEditor content from props for section ${id} in composition ${compositionId}`)
      this.setState({
        editorVal: nextProps.content
      })
    }

    if (currentSectionId === id && currentSectionState === SECTION_STATE.TYPING && nextProps.currentSectionState !== SECTION_STATE.TYPING) {
      logger.info(`[Editor] Flushing content from typing for section ${id} in composition ${compositionId}`)
      console.info('[Editor] Flushing content from typing')
      updateSectionFlush()
    }
  }

  handleChange = (newValue, cursorPosition, content) => {
    const { updateValueOnEdit } = this.props
    if (newValue !== this.state.editorVal) {
      if (isAmwellTheme()) {
        handleContentFocus()
      }
      this.setState({ editorVal: newValue })
      updateValueOnEdit(newValue, cursorPosition, content)
    }
  }

  updateCursorTracking = (sectionId, position) => {
    this.props.client.mutate({
      mutation: SET_CURSOR_POSITION,
      variables: {
        sectionId,
        cursorPositionIndex: position,
        location: 'CONTENT'
      }
    })
  }

  onCursorChange = async (range, source) => {
    const {
      id,
      client,
      currentSectionId,
      keyboardMode
    } = this.props

    const stream = getStreamWorker()
    if (isAmwellTheme()) {
      handleContentFocus()
    }
    // TODO: Also need to make sure there is an active section
    // TODO: agent listening is not the best indicator of transcription
    if (!keyboardMode) {
      client.mutate({
        mutation: SET_CURSOR_POSITION,
        variables: {
          sectionId: id,
          cursorPositionIndex: range.index,
          location: 'CONTENT'
        }
      })

      const { data: { audioStreamUIContext } } = await client.query({ query: GET_AUDIO_STREAM_UI_CONTEXT })
      stream && stream.postMessage({
        cmd: DataCommand.UI_CONTEXT,
        uiContext: {
          ...audioStreamUIContext,
          sectionId: id
        },
        ts: new Date().toISOString()
      })

      if (currentSectionId !== id && !keyboardMode) {
        if (!currentSectionId) {
          hs.add(HALF_STORY.CHANGE_SECTION, `Sending CHANGE_SECTION. Entering Section [${id}]`)
        } else {
          hs.add(HALF_STORY.CHANGE_SECTION, `Sending CHANGE_SECTION. Section [${currentSectionId}] => [${id}]`)
        }
        logger.info('SENDING CHANGE_SECTION FROM LINE 122 IN AUDIO MODE ON_CURSOR_CHANGE FUNCTION IN SECTIONEDITOR.JS')
        stream && stream.postMessage({
          cmd: DataCommand.USER_REQUEST,
          userRequestType: 'CHANGE_SECTION',
          userRequestData: {
            // WE SHOULD NOT SEND CHANGE_SECTION REQUEST IF SECTION_ID IS NULL
            sectionId: id
          },
          ts: new Date().toISOString()
        })

        await client.mutate({
          mutation: SET_SECTION,
          variables: {
            id,
            state: SECTION_STATE.DICTATION
          }
        })
      }

      if (source !== 'api' && !keyboardMode) {
        const cursorData = {
          cursorPositionIndex: range.index,
          sectionId: id,
          location: 'CONTENT'
        }

        if (range.length > 0) {
          cursorData.cursorPositionEndIndex = range.index + range.length
        }

        stream && stream.postMessage({
          cmd: DataCommand.CURSOR_POSITION,
          cursorData,
          ts: new Date().toISOString()
        })

        if (range.length > 0) {
          client.mutate({
            mutation: SET_CURSOR_ACKNOWLEDGED,
            variables: {
              isAcknowledged: false
            }
          })
        }
      }
    }
  }

  // Everytime the section is in focus this function gets triggered
  // We put the user in keyboard mode only when they type in a section
  // We will start sending cursor positions when in dictation mode as well onClick

  // The cursor positions will get sent only AFTER a section is in focus and not the
  // first time a section comes into focus.

  // When we put a section in focus on click, we have to ENABLE CLICK TO DICTATE
  // Which involves sending a CHANGE_SECTION to the backend, followed by a ui_context shift
  onHandleFocus = () => {
    const {
      agentState,
      keyboardMode,
      id,
      currentSectionId,
      compositionId
    } = this.props

    console.info('IN SECTION EDITOR ON HANDLE FOCUS.')
    if (isAmwellTheme()) {
      handleContentFocus()
    }
    const stream = getStreamWorker()
    if (!stream) return

    if (agentState !== 'LISTENING' && !keyboardMode && id) {
      logger.info(`[S2 ALERT - CLIENT WAS IN AUDIO MODE & WE RECEIVED A FOCUS ON SECTION ${id}]`)

      if (id !== currentSectionId) {
        // Set CLICK TO DICTATE to current section
        hs.add(HALF_STORY.CHANGE_SECTION, `Sending CHANGE_SECTION. Section [${currentSectionId}] => [${id}]`)
        logger.info('SENDING CHANGE_SECTION FROM LINE 200 IN AUDIO MODE ONHANDLEFOCUS FUNCTION IN SECTIONEDITOR.JS')
        setSectionDictation(compositionId, id)
        const stream = getStreamWorker()
        stream && id && stream.postMessage({
          cmd: DataCommand.USER_REQUEST,
          userRequestType: 'CHANGE_SECTION',
          userRequestData: {
            // WE SHOULD NOT SEND CHANGE_SECTION REQUEST IF SECTION_ID IS NULL
            sectionId: id
          },
          ts: new Date().toISOString()
        })
      }
    }
  }

  onExitTyping = e => {
    // This also happens we enter a section, so if in dictation clear current section
    // Let's just not render the thick section selector & put the section out of
    const { agentState, clearCurrentSection } = this.props
    if (agentState !== 'LISTENING') {
      clearCurrentSection()
    }
  }

  exitSpeechEnterTyping = async key => {
    const {
      client,
      setAudioStreamContext,
      id,
      agentState
    } = this.props

    // Set local AUDIO_PROCESSED state to false
    await client.mutate({
      mutation: SET_AUDIO_PROCESSED,
      variables: {
        isProcessed: false
      }
    })

    client.mutate({
      mutation: SET_CURSOR_ACKNOWLEDGED,
      variables: {
        isAcknowledged: true
      }
    })

    setAudioStreamContext({
      agentAwake: false,
      transcription: false,
      isScriptChange: false,
      section: id
    }).then(async () => {
      sendSpeechConfig('Sending new speech config on STOP_DICTATION')
      // no need to clear section or pass current section id
      stopSectionDictation('hm', false, id)

      // Wait for stop dictation and then enteggr typing & send audio_end
      // Put user in keyboard mode
      if (agentState !== AGENT.STATE.MUTED) {
        await client.mutate({
          mutation: SET_AGENT_STATE,
          variables: {
            agentState: AGENT.STATE.MUTED
          }
        })
        // Turning off the badge above should trigger KEYBOARD_ON
        await client.mutate({
          mutation: SET_SECTION,
          variables: {
            id,
            state: SECTION_STATE.TYPING
          }
        })
      }
      client.mutate({
        mutation: SET_KEYBOARD_MODE
      })
      seshSto.session(SESH_STO.KEYBOARD_MODE, true)
      logger.info('[S2 ALERT - CLIENT ENTERING KEYBOARD MODE AND TURNING OFF THE BADGE]')

      // focus in current section
      const sectionEditorEl = document.getElementById(`sectionEditor-${id}`)
      if (sectionEditorEl) {
        sectionEditorEl.focus()
        logger.info(`Focused section ${id} after section was typed into [DOM ID=${sectionEditorEl.id}]`)
      }
    })
  }

  onHandleUnidecode = (event) => {
    const noteId = loSto.get(LO_STO.CURRENT_COMP_ID)
    const { beforeText, afterText } = event

    logger.info(`[Unidecode Conversion] - NoteId: ${noteId} | Before: ${beforeText} | After: ${afterText} `)
    setToast(TOAST.TYPE.INFO, 'Converted unsupported text', TOAST.DURATION.SHORT)
  }

  clearContentToUpdate = () => {
    this.props.client.mutate({
      mutation: SET_CONTENT_TO_INSERT,
      variables: {
        sections: null
      }
    })
  }

  clickInsertScript = async (scriptId, currentSectionId) => {
    const { client } = this.props

    const compositionId = loSto.get(LO_STO.CURRENT_COMP_ID)

    // get notetypeid
    const { composition } = await client.readQuery({
      query: GET_CURRENT_COMPOSITION
    })
    const comp = JSON.parse(composition, null, 2)
    const sectionData = comp.sectionsS2.find(section => section.id === currentSectionId)

    client.mutate({
      mutation: SET_INSERT_SCRIPT_PENDING,
      variables: {
        isPending: true
      }
    })

    hs.add(
      HALF_STORY.CLICK_INSERT_SCRIPT,
      'Click Insert Script in Doctor Script Editor'
    )
    const stream = getStreamWorker()
    stream && stream.postMessage({
      cmd: DataCommand.USER_REQUEST,
      userRequestType: 'CLICK_INSERT_SCRIPT',
      userRequestData: {
        compositionId,
        notetypeId: comp?.metadata?.noteTypeId,
        scriptId: scriptId,
        sectionId: currentSectionId
      },
      ts: new Date().toISOString()
    })
  };

  render () {
    const {
      editorVal
    } = this.state
    const {
      id,
      name,
      currentSectionId,
      isProcessed,
      cursorPosition,
      agentState,
      cursorPositions,
      audioStreamUIContext,
      keyboardMode,
      scripts,
      contentToInsert,
      handleTabKeyPress
    } = this.props

    const content = editorVal

    let startDictationCursor = null
    if (currentSectionId === id) {
      startDictationCursor = cursorPositions.find(cp => cp.sectionId === id && cp.location === 'CONTENT')
    }

    // keyboardMode will be true only when we locally set the state to keyboardMode
    const isWaitingForAudioProcessed = (currentSectionId === id) && !isProcessed && keyboardMode

    const isReadOnly = id !== currentSectionId && !keyboardMode

    return (
      <Container isAmwellTheme={isAmwellTheme()}>
        {isWaitingForAudioProcessed &&
          <div style={{ margin: 'auto' }}>
            <Loading />
          </div>}
        <Editor
          value={content}
          onChange={this.handleChange}
          persona={SUKI_ROLE.PHYSICIAN}
          s2Mode
          handleFocus={this.onHandleFocus}
          handleBlur={this.onExitTyping}
          handleUnidecode={this.onHandleUnidecode}
          handleCursorChange={this.onCursorChange}
          isWaitingForAudioProcessed={!isProcessed}
          startDictationCursor={startDictationCursor && startDictationCursor.cursorPositionIndex}
          cursorPosition={cursorPosition}
          id={id}
          name={name}
          currentSectionId={currentSectionId}
          exitSpeechEnterTyping={this.exitSpeechEnterTyping}
          isSpeech={agentState === 'LISTENING'}
          isMuted={agentState === 'MUTED'}
          isTranscription={audioStreamUIContext?.transcription}
          isNewSection={name === ''}
          updateCursorTracking={this.updateCursorTracking}
          readOnly={isReadOnly}
          enableUnidecoding
          scripts={scripts}
          contentToInsert={contentToInsert}
          clearContentToUpdate={this.clearContentToUpdate}
          getMacroContent={getMacroContent}
          handleTabKeyPress={handleTabKeyPress}
          clickInsertScript={this.clickInsertScript}
        />
      </Container>
    )
  }
}

export default compose(
  graphql(GET_AUDIO_PROCESSED, {
    props: ({ data: { isProcessed } }) => ({ isProcessed })
  }),
  graphql(GET_CURSOR_POSITION, {
    props: ({ data: { cursorPositions } }) => {
      return ({
        cursorPositions
      })
    }
  }),
  graphql(SET_AUDIO_STREAM_UI_CONTEXT, {
    props: ({ mutate }) => ({
      setAudioStreamContext: context => mutate({
        variables: { audioStreamUIContext: context }
      })
    })
  }),
  graphql(GET_AUDIO_STREAM_UI_CONTEXT, {
    props: ({ data: { audioStreamUIContext } }) => ({
      audioStreamUIContext
    })
  }),
  graphql(GET_KEYBOARD_MODE, {
    props: ({ data: { keyboardMode } }) => {
      return { keyboardMode }
    }
  }),
  graphql(GET_CURRENT_NOTETYPE_ID, {
    props: ({ data: { currentNoteTypeId } }) => {
      return { currentNoteTypeId }
    }
  }),
  graphql(CLEAR_SECTION, {
    props: ({ mutate }) => ({
      clearCurrentSection: () => mutate()
    })
  }),
  withApollo
)(SectionEditor)
